'use client';

import React from 'react';
import {
  IconVerified,
  IconFuture,
  IconTopRated,
  IconRentStabilized,
  IconGoodCause,
  IconWorst,
} from '@repo/svgs';
import type { Listing } from '@repo/api';
import type { TagVariant } from '@repo/utils';
import { ListCell } from '@/components/ui';
import { Divider } from '@/components/ui';
import { ResponsiveLink } from '@/components/shared/ResponsiveLink';
import { RentStabilizedDownloadModal } from '@/components/modal/RentStabilizedDownloadModal';

const ICON_BY_VARIANT: Record<
  TagVariant,
  React.ComponentType<{
    width?: number;
    height?: number;
    style?: React.CSSProperties | { color?: string };
    className?: string;
  }>
> = {
  verified: IconVerified,
  topRated: IconTopRated,
  rentStabilized: IconRentStabilized,
  goodCause: IconGoodCause,
  future: IconFuture,
  worstLandlord: IconWorst,
};

interface UnitHighlightsProps {
  listing: Listing;
}

const HIGHLIGHT_CONFIGS: Array<{
  key: string;
  variant: TagVariant;
  title: string;
  description: string;
  href?: string;
  condition: (listing: Listing) => boolean;
}> = [
  {
    key: 'future',
    variant: 'future',
    title: 'Future apartment listing',
    description:
      'This apartment is not available yet, but it can be matched to you before it hits the market.',
    condition: l => l.activeListing?.isFutureListing || false,
  },
  {
    key: 'verified',
    variant: 'verified',
    title: 'Verified apartment listing',
    href: '/listings/status:verified',
    description:
      'This apartment has confirmed availability and you can apply to this apartment directly.',
    condition: l => (!l.activeListing?.isFutureListing && l.isBuildingVerified) || false,
  },
  {
    key: 'topRated',
    variant: 'topRated',
    title: 'Top rated building',
    href: '/listings/status:top-rated',
    description: 'This building is highly reviewed and rated 4+ stars by past and current renters.',
    condition: l => (l.buildingAverageRating ?? 0) >= 4.3 && (l.buildingReviewCount ?? 0) > 10,
  },
  {
    key: 'rentStabilized',
    variant: 'rentStabilized',
    title: 'Rent-stabilized apartments',
    href: '/listings/status:rent-stabilized',
    description:
      'This building has apartments that entitle you to a renewal and limited rent increases.',
    condition: l => l.isBuildingRentStabilized || false,
  },
  {
    key: 'goodCause',
    variant: 'goodCause',
    title: 'Good cause building',
    href: '/listings/status:good-cause',
    description:
      'This building guarantees a renewal and capped rent increases, if you follow your lease terms.',
    condition: l => l.hasBuildingGoodCauseEviction || false,
  },
  {
    key: 'worstLandlord',
    variant: 'worstLandlord',
    title: 'Worst landlord',
    description: "This apartment is associated with one of NYC's 100 worst landlords.",
    condition: l => l.building?.latestWorstLandlordsYear != null,
  },
];

const ICON_CLASS_BY_VARIANT: Record<TagVariant, string> = {
  verified: 'icon-verified',
  topRated: 'icon-top-rated',
  rentStabilized: 'icon-rent-stabilized',
  goodCause: 'icon-good-cause',
  future: 'icon-future',
  worstLandlord: 'icon-alert',
};

export function UnitHighlights({ listing }: UnitHighlightsProps) {
  const availableHighlights = HIGHLIGHT_CONFIGS.filter(config => config.condition(listing));

  // Rent-stabilized filter on web is a dead-end (the full rent-stab
  // browsing experience lives in the app). Instead of navigating to
  // /listings/status:rent-stabilized, clicking the row opens the
  // shared download-prompt modal — same pattern as the home-page gem
  // card and listings filter chip.
  const [rentStabModalOpen, setRentStabModalOpen] = React.useState(false);

  if (availableHighlights.length === 0) {
    return null;
  }

  return (
    <div className="w-full md:px-2">
      {availableHighlights.map((config, index) => {
        const IconComponent = ICON_BY_VARIANT[config.variant];
        const iconClass = ICON_CLASS_BY_VARIANT[config.variant];
        const leadingIcon = IconComponent ? (
          <IconComponent width={24} height={24} className={iconClass} />
        ) : undefined;

        const isRentStab = config.key === 'rentStabilized';

        return (
          <React.Fragment key={config.key}>
            {isRentStab ? (
              // Intercept — no navigation, open the download modal.
              <ListCell
                leadingIcon={leadingIcon}
                title={config.title}
                subtitle={config.description}
                trailing="chevron"
                onClick={() => setRentStabModalOpen(true)}
              />
            ) : config.href ? (
              <ResponsiveLink href={config.href} className="block">
                <ListCell
                  leadingIcon={leadingIcon}
                  title={config.title}
                  subtitle={config.description}
                  trailing="chevron"
                />
              </ResponsiveLink>
            ) : (
              <ListCell
                leadingIcon={leadingIcon}
                title={config.title}
                subtitle={config.description}
                trailing="none"
                className="hover:!bg-primary !cursor-default"
              />
            )}

            {index < availableHighlights.length - 1 && (
              <Divider variant="tertiary" className="my-1" />
            )}
          </React.Fragment>
        );
      })}

      <RentStabilizedDownloadModal
        open={rentStabModalOpen}
        onClose={() => setRentStabModalOpen(false)}
        source="unit_page_rent_stab"
      />
    </div>
  );
}
